import { HttpErrorResponse } from '@angular/common/http';
import { Component, ChangeDetectorRef, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgClass } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ButtonComponent,
	INavHelp,
	ISelect,
	NavHelpComponent,
	ProgressComponent,
	PwcsLayoutComponent,
} from '@tecalisdev/infinity';
import { IToggleAccept } from '@tecalisdev/infinity/lib/interfaces/toggleAccept';
import { Subject } from 'rxjs';
import { IFormProgressConf, ProgressService } from 'src/app/core/helpers/progress.service';
import { NavHelpAction } from 'src/app/core/models/navHelp.models';
import { AuthService } from 'src/app/core/services/auth.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { ModalScanQrComponent } from 'src/app/shared/components/modal-scan-qr/modal-scan-qr.component';
import { DesignService } from 'src/app/shared/services/config/design.service';
import { ErrorService } from 'src/app/shared/services/error.service';
import { MasterService } from 'src/app/shared/services/master.service';
import { NavHelpService } from 'src/app/shared/services/nav-help.service';
import { LanguagePickerUiService } from 'src/app/shared/services/language-picker-ui.service';
import {
	IFinishOperation,
	IFormFieldConfig,
	IFormOperation,
	IFormStepConfig,
	IOperation,
	LocalizedText,
	OPERATION_STATUS,
	OPERATION_TYPE,
	PAGE_WARNING,
	ProcessLog,
} from '../../models/user-identification';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import { ProcessService } from '../../services/process.service';
import { DynamicFormService } from '../../services/dynamic-form.service';
import { ProvinceService } from '../../services/province.service';
import { phoneE164Validator } from 'src/app/shared/validators/phone-e164.validator';
import { getFormSteps } from '../../utils/form-operation.util';
import {
	coerceFormSelectDefaultValue,
	isCatalogBackedFormField,
	isProvinceFormField,
	resolveFormFieldSelectPlaceholder,
	resolveRawFormFieldOptions,
	usesFormFieldSelectSearch,
} from '../../utils/form-field-options.util';
import {
	bindCountryProvinceCascade,
	ProvinceSelectUiState,
} from '../../utils/country-province-cascade.util';
import {
	coerceProvinceSelectField,
	dynamicFormLegalControlKey,
	dynamicFormLegalControlKeys,
	dynamicFormFieldGridColClass,
	dynamicFormInputType,
	dynamicFormLegalSelectionValidator,
	dynamicFormNonEmptyArrayValidator,
	hasWorkflowOcrOperation,
	resolveDynamicFormStepDescription,
	stepHasCountryProvinceCascade,
	usesDynamicFormBooleanCheckbox,
	isDynamicFormDateField,
	isDynamicFormEmailField,
	isDynamicFormFieldMultiple,
	isDynamicFormFieldRequired,
	isDynamicFormMultiChoiceField,
	isDynamicFormPhoneField,
	isDynamicFormPhonePrefixField,
	serializeDynamicFormDateValue,
	usesDynamicFormLegalField,
	usesDynamicFormSelectDropdown,
	usesDynamicFormWideFieldsLayout,
} from '../../utils/dynamic-form-field.util';
import { DynamicFormHelpComponent } from './dynamic-form-help/dynamic-form-help.component';
import { DynamicFormIntroComponent } from './dynamic-form-intro/dynamic-form-intro.component';
import { DynamicFormStepComponent } from './dynamic-form-step/dynamic-form-step.component';

@Component({
	selector: 'app-dynamic-form',
	imports: [
		NgClass,
		TranslatePipe,
		PwcsLayoutComponent,
		ProgressComponent,
		NavHelpComponent,
		ButtonComponent,
		ModalScanQrComponent,
		DynamicFormIntroComponent,
		DynamicFormHelpComponent,
		DynamicFormStepComponent,
	],
	templateUrl: './dynamic-form.component.html',
	styleUrl: './dynamic-form.component.scss',
})
export class DynamicFormComponent implements OnInit {
	@Input() operations: IOperation[] = [];
	@Input() operation!: IFormOperation;
	/** Evita POST /operation/start en sandbox dev (/models/form). */
	@Input() skipProcessInit = false;
	/** Omite pantalla intro (p. ej. sandbox con muchos pasos). */
	@Input() skipIntro = false;

	private dynamicFormService = inject(DynamicFormService);
	private provinceService = inject(ProvinceService);
	private processService = inject(ProcessService);
	private cloudLogsService = inject(CloudLogsService);
	private masterService = inject(MasterService);
	private errorService = inject(ErrorService);
	private navHelpService = inject(NavHelpService);
	private languagePickerUi = inject(LanguagePickerUiService);
	private languageService = inject(LanguageService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);
	private cdr = inject(ChangeDetectorRef);
	public auth = inject(AuthService);

	public designService = inject(DesignService);
	public progressService = inject(ProgressService);

	public navsHelp: Array<INavHelp> = this.navHelpService.getNavsHelp([NavHelpAction.VOLUMEN]);
	public showIntro = signal(false);
	public showFormHelp = signal(false);
	public isVisibleScan = false;
	public currentStepIndex = signal(0);
	public submitting = signal(false);
	/** Errores de campo solo tras Continuar (NIF-824); no en blur/dirty. */
	public showValidationErrors = signal(false);
	public apiError = signal<string | null>(null);
	public layoutSubtitle = '';
	public stepForm = new FormGroup({});
	public legalToggleOptions: Record<string, IToggleAccept[]> = {};
	public stepFieldOptions: Record<string, ISelect[]> = {};
	public stepFieldSelectSearch: Record<string, boolean> = {};
	public stepFieldSelectPlaceholder: Record<string, string> = {};
	public stepFieldSelectDisabled: Record<string, boolean> = {};
	/** Cascada país→provincia activa en el paso actual. */
	public countryProvinceCascade = false;

	private stepValues = new Map<string, Record<string, unknown>>();
	private processInitDone = false;
	/** Cancela suscripciones de cascada al reconstruir el paso. */
	private cascadeDestroy$ = new Subject<void>();

	public ngOnInit(): void {
		this.languageService.languageChanged$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.refreshLocalizedView();
			});

		this.destroyRef.onDestroy(() => {
			this.cascadeDestroy$.next();
			this.cascadeDestroy$.complete();
		});

		this.restoreResumeStep();
		this.updateLayoutSubtitle();
		if (this.shouldShowIntro()) {
			this.showIntro.set(true);
			this.initProcessOperation();
			return;
		}
		this.buildStepForm();
		this.initProcessOperation();
	}

	private initProcessOperation(): void {
		if (this.skipProcessInit || this.processInitDone) {
			return;
		}

		this.masterService
			.getMaster()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((master) => {
				if (!master || this.processInitDone) {
					return;
				}
				this.processInitDone = true;
				this.processService.initOperation(OPERATION_TYPE.FORM);
			});
	}

	public get steps(): IFormStepConfig[] {
		return getFormSteps(this.operation?.config);
	}

	public get currentStep(): IFormStepConfig | undefined {
		return this.steps[this.currentStepIndex()];
	}

	public get isLastStep(): boolean {
		return this.currentStepIndex() >= this.steps.length - 1;
	}

	public get canGoBack(): boolean {
		return this.currentStepIndex() > 0;
	}

	public get progressConf(): IFormProgressConf {
		return { formStepIndex: this.currentStepIndex() };
	}

	public get currentStepTitle(): string {
		return this.resolveText(this.currentStep?.title);
	}

	public get currentStepDescription(): string {
		return resolveDynamicFormStepDescription(
			this.currentStep,
			hasWorkflowOcrOperation(this.operations),
			(value) => this.resolveText(value),
			(key) => this.trans.instant(key),
		);
	}

	public usesWideFieldsLayout(): boolean {
		return usesDynamicFormWideFieldsLayout(this.currentStep?.fields ?? []);
	}

	public resolveText = (value?: LocalizedText | null): string => {
		return this.languageService.resolveLocalizedText(value);
	};

	public startForm(): void {
		this.showIntro.set(false);
		this.updateLayoutSubtitle();
		this.buildStepForm();
	}

	public goBack(): void {
		if (this.showFormHelp()) {
			this.showFormHelp.set(false);
			return;
		}
		if (!this.canGoBack || this.submitting()) {
			return;
		}
		this.persistCurrentStepValues();
		this.currentStepIndex.update((index) => index - 1);
		this.buildStepForm();
		this.apiError.set(null);
	}

	public submitStep(): void {
		this.normalizeMultiChoiceControls(this.currentStep);
		if (this.stepForm.invalid || this.submitting() || !this.currentStep) {
			this.showValidationErrors.set(true);
			this.stepForm.markAllAsTouched();
			return;
		}

		const step = this.currentStep;
		this.persistCurrentStepValues();
		const fields = step.fields.flatMap((field: IFormFieldConfig) => this.buildSubmitFields(field));

		this.submitting.set(true);
		this.apiError.set(null);

		this.dynamicFormService
			.submit(this.operation.uuid, {
				responses: [{ step_uuid: step.uuid, fields }],
				complete: this.isLastStep,
			})
			.subscribe({
				next: (response) => {
					this.submitting.set(false);
					if (response?.data?.current_step_order) {
						this.operation.current_step_order = response.data.current_step_order;
					}
					if (this.isLastStep) {
						if (response?.data?.state === OPERATION_STATUS.KO) {
							this.verificationFail(OPERATION_STATUS.KO);
							return;
						}
						this.operation.completed = true;
						return;
					}
					this.currentStepIndex.update((index) => index + 1);
					this.buildStepForm();
				},
				error: (error: HttpErrorResponse) => {
					this.submitting.set(false);
					const retry = error?.error?.retry ?? false;
					if (error?.error?.result === OPERATION_STATUS.KO && !retry) {
						this.verificationFail(OPERATION_STATUS.KO);
						return;
					}
					this.apiError.set(error?.error?.message ?? 'FORM.SUBMIT_ERROR');
					this.errorService.hasGeneralError({ ...error?.error });
				},
			});
	}

	public onLegalToggleChange(event: { controlKey: string; options: IToggleAccept[] }): void {
		const control = this.stepForm.get(event.controlKey);
		if (!control) {
			return;
		}
		const found = event.options.find((opt) => opt.value);
		if (!found) {
			control.setValue(null);
		} else {
			control.setValue(found.id === 'yes');
		}
		control.markAsDirty();
		control.markAsTouched();
		control.updateValueAndValidity();
		this.legalToggleOptions[event.controlKey] = event.options;
	}

	public onUploadFiles(event: { fieldName: string; files: FileList }): void {
		const file = event.files?.[0];
		if (!file) {
			return;
		}

		const reader = new FileReader();
		reader.onload = () => {
			this.stepForm
				.get(event.fieldName)
				?.setValue(typeof reader.result === 'string' ? reader.result : null);
		};
		reader.readAsDataURL(file);
	}

	public navHelpChange(action: INavHelp): void {
		const actionId = typeof action === 'string' ? action : action?.id;
		const actions: Partial<Record<NavHelpAction, () => void>> = {
			[NavHelpAction.QR]: () => (this.isVisibleScan = true),
			[NavHelpAction.HELP]: () => this.showFormHelp.set(true),
			[NavHelpAction.LANGUAGE]: () => this.languagePickerUi.open(),
		};
		if (actionId) {
			actions[actionId as NavHelpAction]?.();
		}
	}

	public openScanFromHelp(): void {
		this.showFormHelp.set(false);
		this.isVisibleScan = true;
	}

	// Delegaciones para tests existentes.
	public isDateField(field: IFormFieldConfig): boolean {
		return isDynamicFormDateField(field);
	}

	public isPhonePrefixField(field: IFormFieldConfig): boolean {
		return isDynamicFormPhonePrefixField(field);
	}

	public isNumberField(field: IFormFieldConfig): boolean {
		return field.id === 'number' || field.name === 'number';
	}

	public inputType(field: IFormFieldConfig): 'text' | 'email' | 'number' {
		return dynamicFormInputType(field);
	}

	public usesSelectDropdown(field: IFormFieldConfig): boolean {
		return usesDynamicFormSelectDropdown(field, this.fieldOptions(field).length);
	}

	public usesToggleField(field: IFormFieldConfig): boolean {
		return field.type === 'radio';
	}

	public fieldOptions(field: IFormFieldConfig): ISelect[] {
		return this.stepFieldOptions[field.uuid] ?? [];
	}

	public usesSelectSearch(field: IFormFieldConfig): boolean {
		return this.stepFieldSelectSearch[field.uuid] ?? false;
	}

	public fieldHasError(field: IFormFieldConfig): boolean {
		const control = this.stepForm.get(field.name);
		return Boolean(control && control.invalid && this.showValidationErrors());
	}

	public fieldGridColClass(field: IFormFieldConfig): string {
		return dynamicFormFieldGridColClass(field);
	}

	public isFieldRequired(field: IFormFieldConfig): boolean {
		return isDynamicFormFieldRequired(field);
	}

	public fieldGroupLabel(field: IFormFieldConfig): string {
		const label = this.resolveText(field.title);
		return this.isFieldRequired(field) && label ? `${label}*` : label;
	}

	private shouldShowIntro(): boolean {
		if (this.skipIntro || this.operation?.config?.tutorial === false) {
			return false;
		}
		const resumeOrder = this.operation?.current_step_order;
		return !resumeOrder || resumeOrder < 1;
	}

	private refreshLocalizedView(): void {
		this.updateLayoutSubtitle();

		if (this.currentStep) {
			const snapshot = this.stepForm.getRawValue();
			this.buildStepForm();
			this.stepForm.patchValue(snapshot, { emitEvent: false });
			this.refreshLegalToggleOptions();
		}

		this.cdr.detectChanges();
	}

	private updateLayoutSubtitle(): void {
		this.layoutSubtitle = '';
	}

	private refreshLegalToggleOptions(): void {
		this.syncLegalToggleOptionsFromControls(this.currentStep);
	}

	/** Sincroniza el estado visual del toggle legal con el valor del control (true/false/null). */
	private syncLegalToggleOptionsFromControls(step?: IFormStepConfig): void {
		step?.fields?.forEach((field) => {
			if (!usesDynamicFormLegalField(field)) {
				return;
			}
			dynamicFormLegalControlKeys(field).forEach((controlKey) => {
				this.legalToggleOptions[controlKey] = this.buildLegalToggleOptionsFromControl(controlKey);
			});
		});
	}

	private buildLegalToggleOptionsFromControl(controlKey: string): IToggleAccept[] {
		const options = this.buildYesNoOptions();
		const value = this.stepForm.get(controlKey)?.value;
		if (value === true) {
			const yes = options.find((option) => option.id === 'yes');
			if (yes) {
				yes.value = true;
			}
		} else if (value === false) {
			const no = options.find((option) => option.id === 'no');
			if (no) {
				no.value = true;
			}
		}
		return options;
	}

	private restoreResumeStep(): void {
		const resumeOrder = this.operation.current_step_order;
		if (!resumeOrder || resumeOrder < 1 || !this.steps.length) {
			return;
		}
		this.currentStepIndex.set(Math.min(resumeOrder - 1, this.steps.length - 1));
	}

	private persistCurrentStepValues(): void {
		const step = this.currentStep;
		if (!step) {
			return;
		}

		const values: Record<string, unknown> = {};
		step.fields.forEach((field) => {
			if (isDynamicFormPhonePrefixField(field)) {
				return;
			}
			if (usesDynamicFormLegalField(field)) {
				dynamicFormLegalControlKeys(field).forEach((key) => {
					values[key] = this.stepForm.get(key)?.value ?? null;
				});
				return;
			}
			values[field.name] = this.stepForm.get(field.name)?.value ?? null;
		});
		this.stepValues.set(step.uuid, values);
	}

	private buildStepForm(): void {
		this.cascadeDestroy$.next();
		this.showValidationErrors.set(false);

		const step = this.currentStep;
		const controls: Record<string, FormControl> = {};
		const savedValues = step ? this.stepValues.get(step.uuid) : undefined;
		this.countryProvinceCascade = stepHasCountryProvinceCascade(step?.fields);

		step?.fields?.forEach((field: IFormFieldConfig) => {
			if (isDynamicFormPhonePrefixField(field)) {
				return;
			}
			if (usesDynamicFormLegalField(field)) {
				dynamicFormLegalControlKeys(field).forEach((controlKey) => {
					const validators = this.resolveValidators(field);
					const defaultValue = savedValues?.[controlKey] ?? null;
					controls[controlKey] = new FormControl(defaultValue, validators);
				});
				return;
			}

			const validators = this.resolveValidators(field);
			const defaultValue = this.resolveDefaultValue(field, savedValues?.[field.name]);
			const control = new FormControl(defaultValue, validators);
			// NIF-658: identidad autofilled desde OCR no editable.
			if (field.disabled === true) {
				control.disable({ emitEvent: false });
			}
			controls[field.name] = control;
		});

		this.stepForm = new FormGroup(controls);
		this.normalizeMultiChoiceControls(step);
		this.rebuildStepFieldMetadata(step);
		this.syncLegalToggleOptionsFromControls(step);
		this.setupCountryProvinceCascade(step);
	}

	private normalizeMultiChoiceControls(step?: IFormStepConfig): void {
		step?.fields?.forEach((field) => {
			if (!isDynamicFormMultiChoiceField(field)) {
				return;
			}
			const control = this.stepForm.get(field.name);
			if (!control) {
				return;
			}
			const value = control.value;
			if (value === null || value === undefined) {
				control.setValue([], { emitEvent: false });
			}
		});
	}

	private rebuildStepFieldMetadata(step?: IFormStepConfig): void {
		const optionsByField: Record<string, ISelect[]> = {};
		const searchByField: Record<string, boolean> = {};
		const placeholderByField: Record<string, string> = {};
		const disabledByField: Record<string, boolean> = {};
		const deps = this.formFieldOptionsDeps();
		const cascade = stepHasCountryProvinceCascade(step?.fields);

		step?.fields?.forEach((field) => {
			if (isDynamicFormPhonePrefixField(field)) {
				return;
			}

			const renderField = coerceProvinceSelectField(field, cascade);
			const rawOptions = resolveRawFormFieldOptions(renderField, deps);
			const selectOptions = rawOptions.map((option) => ({
				id: option.value,
				label: option.label,
			}));

			optionsByField[field.uuid] = selectOptions;
			searchByField[field.uuid] = usesFormFieldSelectSearch(renderField, selectOptions.length);
			placeholderByField[field.uuid] = resolveFormFieldSelectPlaceholder(renderField, deps);
			disabledByField[field.uuid] = field.disabled === true;
		});

		this.stepFieldOptions = optionsByField;
		this.stepFieldSelectSearch = searchByField;
		this.stepFieldSelectPlaceholder = placeholderByField;
		this.stepFieldSelectDisabled = disabledByField;
	}

	private formFieldOptionsDeps() {
		return {
			translate: (key: string) => this.trans.instant(key),
			resolveLocalizedText: (value?: LocalizedText | null) => this.resolveText(value),
		};
	}

	/** Cascada país → provincia (lógica en `country-province-cascade.util`). */
	private setupCountryProvinceCascade(step?: IFormStepConfig): void {
		bindCountryProvinceCascade(step, {
			stepForm: this.stepForm,
			until$: this.cascadeDestroy$,
			destroyRef: this.destroyRef,
			loadProvinces: (countryCode) => this.provinceService.getProvinces(countryCode),
			onHttpError: (error) => this.errorService.hasGeneralError({ ...error?.error }),
			markForCheck: () => this.cdr.markForCheck(),
			applyProvinceState: (provinceField, state) => this.patchProvinceSelectUi(provinceField, state),
		});
	}

	private patchProvinceSelectUi(provinceField: IFormFieldConfig, state: ProvinceSelectUiState): void {
		this.stepFieldOptions = {
			...this.stepFieldOptions,
			[provinceField.uuid]: state.options,
		};
		this.stepFieldSelectSearch = {
			...this.stepFieldSelectSearch,
			[provinceField.uuid]: state.search,
		};
		this.stepFieldSelectDisabled = {
			...this.stepFieldSelectDisabled,
			[provinceField.uuid]: state.disabled,
		};
	}

	private buildSubmitFields(field: IFormFieldConfig): Array<{ uuid: string; value: string | boolean | null }> {
		if (isDynamicFormPhonePrefixField(field)) {
			return [{ uuid: field.uuid, value: null }];
		}

		if (usesDynamicFormLegalField(field)) {
			if (!field.options?.length) {
				return [
					{
						uuid: field.uuid,
						value: this.serializeFieldValue(field, this.stepForm.get(field.name)?.value),
					},
				];
			}

			const values = field.options.map((option) => {
				const raw = this.stepForm.get(dynamicFormLegalControlKey(field, option.value))?.value;
				return raw === true || raw === false ? raw : null;
			});

			return [
				{
					uuid: field.uuid,
					value:
						field.options.length === 1
							? values[0]
							: isDynamicFormFieldMultiple(field)
								? JSON.stringify(values)
								: values[0],
				},
			];
		}

		return [
			{
				uuid: field.uuid,
				value: this.serializeFieldValue(field, this.stepForm.get(field.name)?.value),
			},
		];
	}

	private buildYesNoOptions(): IToggleAccept[] {
		return [
			{ label: this.trans.instant('GENERAL.BUTTON.NO'), id: 'no', value: false },
			{ label: this.trans.instant('GENERAL.BUTTON.YES'), id: 'yes', value: false },
		];
	}

	private resolveDefaultValue(field: IFormFieldConfig, savedValue?: unknown): unknown {
		if (savedValue !== undefined) {
			return savedValue;
		}
		if (field.default_value !== undefined) {
			// Provincia: opciones llegan async (cascada); no validar contra catálogo vacío.
			if (isProvinceFormField(field)) {
				return field.default_value;
			}
			if (isCatalogBackedFormField(field) || field.type === 'select') {
				const deps = this.formFieldOptionsDeps();
				const renderField = coerceProvinceSelectField(field, this.countryProvinceCascade);
				const optionValues = resolveRawFormFieldOptions(renderField, deps).map((option) => option.value);
				return coerceFormSelectDefaultValue(field, field.default_value, optionValues);
			}
			return field.default_value;
		}
		if (usesDynamicFormLegalField(field)) {
			return null;
		}
		if (isDynamicFormMultiChoiceField(field)) {
			return [];
		}
		if (usesDynamicFormBooleanCheckbox(field)) {
			return false;
		}
		return '';
	}

	private resolveValidators(field: IFormFieldConfig) {
		const validators = [];

		if (isDynamicFormFieldRequired(field)) {
			if (usesDynamicFormLegalField(field)) {
				// Obligatorio = debe responder Sí o No; «No» es válido (no exige aceptación).
				validators.push(dynamicFormLegalSelectionValidator());
			} else if (isDynamicFormMultiChoiceField(field)) {
				validators.push(dynamicFormNonEmptyArrayValidator());
			} else {
				validators.push(Validators.required);
			}
		}

		if (isDynamicFormEmailField(field)) {
			validators.push(Validators.email);
		}

		if (isDynamicFormPhoneField(field)) {
			validators.push(phoneE164Validator());
		}

		return validators;
	}

	private verificationFail(status: OPERATION_STATUS): void {
		this.operation.completed = true;
		this.cloudLogsService.sendCloudLogs(
			{ status, observations: PAGE_WARNING.VERIFICATION_FAIL },
			ProcessLog.FINISH,
			LogType.Warning,
		);
		this.processService.unSubcribeCheckStatus();
		this.operations.forEach((op: IOperation) => {
			if (op.type !== OPERATION_TYPE.FINISH) {
				op.completed = true;
			}
		});
		const foundFinish = this.operations.find((op: IOperation) => op.type === OPERATION_TYPE.FINISH);
		if (foundFinish) {
			(foundFinish as IFinishOperation).config.status = status;
		}
	}

	private serializeFieldValue(field: IFormFieldConfig, raw: unknown): string | boolean | null {
		if (raw === null || raw === undefined || raw === '') {
			return usesDynamicFormBooleanCheckbox(field) ? false : null;
		}

		if (isDynamicFormDateField(field)) {
			return serializeDynamicFormDateValue(raw);
		}

		if (usesDynamicFormBooleanCheckbox(field) || usesDynamicFormLegalField(field)) {
			return Boolean(raw);
		}

		if (Array.isArray(raw)) {
			const values = raw.map((item) =>
				typeof item === 'object' && item !== null && 'id' in item
					? String((item as { id: string }).id)
					: String(item),
			);
			return isDynamicFormFieldMultiple(field) ? JSON.stringify(values) : values[0] ?? null;
		}

		if (typeof raw === 'object' && raw !== null && 'id' in raw) {
			return String((raw as { id: string }).id);
		}

		if (typeof raw === 'boolean') {
			return raw;
		}

		return String(raw);
	}
}
